import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useEffect } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import "../styles/index.css";
import LoginPage from "../features/auth/Login";
import RegisterPage from "../features/auth/Register";
import ForgotPasswordPage from "../features/auth/ForgotPassword";
import ResetPasswordPage from "../features/auth/ResetPassword";
import HomePage from "../features/home/HomePage";
import Dashboard from "../features/Dashboard/Dashboard";
import ProfilePage from "../features/profile/ProfilePage";
import SettingsPage from "../features/settings/SettingsPage";
import TurmasPage from "../features/turmas/TurmasPage";
import ActivitiesPage from "../features/activities/ActivitiesPage";
import ProtectedRoute from "../app/ProtectedRoute";
import { AuthProvider, useAuth } from "../app/AuthContext";
import { SchoolProfileProvider } from "../app/SchoolProfileContext";
import { ThemeProvider } from "../app/ThemeContext";
import { queryClient } from "../lib/queryClient";

function HomePageWrapper() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <HomePage onLoginClick={() => navigate("/login")} />;
}

function LoginPageWrapper() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <LoginPage
      onBackClick={() => navigate("/")}
      onLoginSuccess={() => navigate("/dashboard")}
      onRegisterClick={() => navigate("/register")}
      onForgotPasswordClick={() => navigate("/forgot-password")}
    />
  );
}

function RegisterPageWrapper() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <RegisterPage
      onBackClick={() => navigate("/")}
      onRegisterSuccess={() => navigate("/dashboard")}
      onLoginClick={() => navigate("/login")}
    />
  );
}

function ForgotPasswordPageWrapper() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <ForgotPasswordPage
      onBackClick={() => navigate("/")}
      onLoginClick={() => navigate("/login")}
    />
  );
}

function ResetPasswordPageWrapper() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const hash = window.location.hash;

  const hasRecoveryToken = hash.includes("access_token=") || location.search.includes("recovery=true");

  if (!isAuthenticated && !hasRecoveryToken) {
    return <Navigate to="/login" replace />;
  }

  return (
    <ResetPasswordPage
      onLoginClick={() => navigate("/login")}
      onSuccess={() => navigate("/login")}
    />
  );
}

function SupabaseAuthRedirectHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hash = window.location.hash;
    const search = location.search;
    
    console.log("SupabaseAuthRedirectHandler detectou hash:", hash);
    
    if (hash.includes("type=recovery") || hash.includes("access_token=") || search.includes("recovery=true")) {
      navigate({
        pathname: "/reset-password",
        hash: hash,
        search: search.includes("recovery=true") ? search : "?recovery=true"
      }, { replace: true });
    }
  }, [location, navigate]);

  return null;
}

function DashboardWrapper() {
  const navigate = useNavigate();
  return (
    <Dashboard
      onLogout={() => navigate("/")}
      onProfileClick={() => navigate("/profile")}
      onSettingsClick={() => navigate("/settings")}
      onClassesClick={() => navigate("/turmas")}
      onActivitiesClick={() => navigate("/activities")}
    />
  );
}

function ProfilePageWrapper() {
  const navigate = useNavigate();
  return <ProfilePage onBackClick={() => navigate("/dashboard")} />;
}

function SettingsPageWrapper() {
  const navigate = useNavigate();
  return <SettingsPage onBackClick={() => navigate("/dashboard")} />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <SupabaseAuthRedirectHandler />
            <SchoolProfileProvider>
              <Routes>
                <Route path="/" element={<HomePageWrapper />} />
                <Route path="/login" element={<LoginPageWrapper />} />
                <Route path="/register" element={<RegisterPageWrapper />} />
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPageWrapper />}
                />
                <Route
                  path="/reset-password"
                  element={<ResetPasswordPageWrapper />}
                />

                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardWrapper />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePageWrapper />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <SettingsPageWrapper />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/turmas"
                  element={
                    <ProtectedRoute>
                      <TurmasPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/activities"
                  element={
                    <ProtectedRoute>
                      <ActivitiesPage />
                    </ProtectedRoute>
                  }
                />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </SchoolProfileProvider>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
